import {
    ATTACHABLE_TYPES,
    imageFilesFrom,
    insertAt,
    uploadImage,
    uploadPlaceholder,
} from '../../lib/attachments.js';
import { BaseComponent, css, html, nothing } from '../../lib/lit.js';

/** Distinguishes concurrent uploads' placeholders. Not persisted anywhere. */
let placeholderSequence = 0;

/**
 * <community-markdown-input> — a textarea you can paste or drag a screenshot
 * into.
 *
 * One component rather than three copies of the handling, because there are
 * three places you write feedback — filing, editing, and commenting — and a
 * screenshot must behave the same in all of them.
 *
 * It owns the label as well as the field. With shadow DOM a `<label for>` in the
 * parent cannot reach a textarea in here, so leaving the label outside would
 * silently break the association.
 *
 * Emits `value-changed` with `{ value }` on every edit, typed or pasted. The
 * parent stays the owner of the text; this only reports.
 */
export class CommunityMarkdownInput extends BaseComponent {
    static properties = {
        value: { type: String },
        label: { type: String },
        optional: { type: Boolean },
        placeholder: { type: String },
        rows: { type: Number },
        maxlength: { type: Number },
        inputId: { type: String, attribute: 'input-id' },
        _uploading: { state: true },
        _armed: { state: true },
        _over: { state: true },
        _error: { state: true },
    };

    /**
     * The drop target's own styling.
     *
     * Two states, because a drag has two questions and they get asked in this
     * order: "can I drop a picture here at all?" the moment the drag starts,
     * and "will it land HERE if I let go now?" once it is over the field. A
     * highlight that only appears under the pointer answers the second and
     * leaves the first unanswered until you have already guessed right.
     */
    static styles = css`
        .drop-zone {
            position: relative;
        }
        .drop-zone textarea {
            transition: border-color 0.1s ease, box-shadow 0.1s ease;
        }
        .drop-zone.armed textarea {
            border: 1px dashed var(--bs-primary, #0d6efd);
        }
        .drop-zone.over textarea {
            border: 1px dashed var(--bs-primary, #0d6efd);
            box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        .drop-overlay {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.5rem;
            border-radius: var(--bs-border-radius, 0.375rem);
            background: rgba(13, 110, 253, 0.08);
            color: var(--bs-primary, #0d6efd);
            font-weight: 600;
            /* The overlay sits ON the textarea, so it must not intercept the
               drag events the zone underneath is listening for. */
            pointer-events: none;
        }
    `;

    constructor() {
        super();
        this.value = '';
        this.label = '';
        this.optional = false;
        this.placeholder = '';
        this.rows = 5;
        this.maxlength = 20000;
        this.inputId = 'markdown-input';
        this._uploading = 0;
        this._armed = false;
        this._over = false;
        this._error = '';
        this._disarmTimer = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        // Watched on the document, not on the field: the point is to light the
        // field up the moment a file is dragged ANYWHERE over the page, so a
        // person can see where to aim before they have aimed.
        document.addEventListener('dragover', this._onDocumentDrag);
        document.addEventListener('drop', this._disarm);
        document.addEventListener('dragend', this._disarm);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('dragover', this._onDocumentDrag);
        document.removeEventListener('drop', this._disarm);
        document.removeEventListener('dragend', this._disarm);
        clearTimeout(this._disarmTimer);
    }

    /**
     * Arm while a file drag is in progress, and disarm shortly after it stops.
     *
     * The timer is doing real work: a drag can end in ways that fire nothing
     * here — dropped on another element, cancelled with Escape, dragged out of
     * the window — and `dragover` repeats every frame while one is live. So
     * "still arriving" is the signal, and its absence is the end. `drop` and
     * `dragend` still disarm immediately when they do fire.
     * @param {DragEvent} event
     */
    _onDocumentDrag = (event) => {
        if (!event.dataTransfer?.types?.includes('Files')) return;
        this._armed = true;
        clearTimeout(this._disarmTimer);
        this._disarmTimer = setTimeout(this._disarm, 150);
    };

    _disarm = () => {
        clearTimeout(this._disarmTimer);
        this._armed = false;
        this._over = false;
    };

    /** @returns {HTMLTextAreaElement} */
    get _textarea() {
        return this.renderRoot.querySelector('textarea');
    }

    /**
     * Report the text as it now stands.
     *
     * Reads the live element rather than `this.value`, because an upload
     * finishing has to act on what the person has typed SINCE it started — and
     * the property only catches up a render after the parent hears about it.
     */
    _emit() {
        this.value = this._textarea.value;
        this.dispatchEvent(
            new CustomEvent('value-changed', {
                detail: { value: this.value },
                bubbles: true,
                composed: true,
            }),
        );
    }

    /**
     * Write `next` into the field and report it, putting the caret at `caret`.
     *
     * Sets the DOM directly for the same reason `_emit` reads it: the parent's
     * round trip is a render away, and a second upload finishing in between must
     * see the first one's text.
     * @param {string} next
     * @param {number} caret
     */
    _setValue(next, caret) {
        const textarea = this._textarea;
        textarea.value = next;
        if (typeof caret === 'number') textarea.setSelectionRange(caret, caret);
        this._emit();
    }

    /**
     * Take the images out of a paste or drop and upload them.
     *
     * Only calls preventDefault when there is actually an image: pasting text
     * into a textarea must keep working, and so must dropping one.
     * @param {ClipboardEvent|DragEvent} event
     */
    async _handleImages(event) {
        const files = imageFilesFrom(event);
        if (files.length === 0) return;

        event.preventDefault();
        this._disarm();
        this._error = '';
        await Promise.all(files.map((file) => this._upload(file)));
    }

    /**
     * Upload one image, holding its place in the text meanwhile.
     *
     * The placeholder goes in at the caret straight away and is swapped for the
     * real Markdown when the URL arrives — so the caret stays put, the person
     * can keep typing, and two screenshots at once land in the order they were
     * pasted rather than wherever the caret has since wandered.
     * @param {File} file
     */
    async _upload(file) {
        const name = file.name || 'screenshot.png';
        const marker = uploadPlaceholder(String(++placeholderSequence), name);

        const textarea = this._textarea;
        const { value, caret } = insertAt(
            textarea.value,
            textarea.selectionStart,
            textarea.selectionEnd,
            marker,
        );
        this._setValue(value, caret);
        this._setUploading(this._uploading + 1);

        try {
            const markdown = await uploadImage(file);
            this._replaceMarker(marker, markdown);
        } catch (err) {
            // Take the placeholder back out: leaving it would post as literal
            // text, and it says "uploading" about something that never will.
            this._replaceMarker(marker, '');
            this._error = /** @type {any} */ (err).message || String(err);
        } finally {
            this._setUploading(this._uploading - 1);
        }
    }

    /**
     * Track in-flight uploads and tell the parent, which owns the submit button.
     *
     * It has to be told rather than asked: submitting mid-upload would file the
     * placeholder as literal text — and on an item with a product, relay that
     * text to a GitHub issue nobody can go back and fix.
     * @param {number} count
     */
    _setUploading(count) {
        const was = this._uploading > 0;
        this._uploading = count;
        const now = this._uploading > 0;
        if (was === now) return;
        this.dispatchEvent(
            new CustomEvent('uploading-changed', {
                detail: { uploading: now },
                bubbles: true,
                composed: true,
            }),
        );
    }

    /**
     * Swap one placeholder for its result, leaving the caret alone unless it sat
     * after the edit — in which case it shifts by the change in length, so text
     * typed while the upload ran does not jump around underneath it.
     * @param {string} marker
     * @param {string} replacement
     */
    _replaceMarker(marker, replacement) {
        const textarea = this._textarea;
        const index = textarea.value.indexOf(marker);
        // Gone: the person deleted the placeholder, or cleared the field. Their
        // edit wins — do not put the image back.
        if (index < 0) return;

        // An emptied placeholder takes its own trailing newline with it.
        const width =
            replacement === '' && textarea.value[index + marker.length] === '\n'
                ? marker.length + 1
                : marker.length;
        const next =
            textarea.value.slice(0, index) + replacement + textarea.value.slice(index + width);

        const caret = textarea.selectionStart;
        const shifted = caret > index ? caret + (replacement.length - width) : caret;
        this._setValue(next, Math.max(0, Math.min(shifted, next.length)));
    }

    /** @param {Event} event */
    async _choose(event) {
        const input = /** @type {HTMLInputElement} */ (event.target);
        const files = Array.from(input.files ?? []);
        // Let the same file be picked twice in a row — the input keeps its
        // value otherwise, and the second pick fires no change event.
        input.value = '';
        this._error = '';
        await Promise.all(files.map((file) => this._upload(file)));
    }

    render() {
        return html`
            ${
                this.label
                    ? html`<label class="form-label" for=${this.inputId}>
                          ${this.label}
                          ${
                              this.optional
                                  ? html`<span class="text-muted fw-normal">(optional)</span>`
                                  : nothing
                          }
                      </label>`
                    : nothing
            }
            <div
                class="drop-zone ${this._armed ? 'armed' : ''} ${this._over ? 'over' : ''}"
                @drop=${(e) => this._handleImages(e)}
                @dragover=${(e) => {
                    if (!e.dataTransfer?.types?.includes('Files')) return;
                    // Without this the browser refuses the drop and opens the
                    // image in the tab instead.
                    e.preventDefault();
                    this._over = true;
                }}
                @dragleave=${(e) => {
                    // Moving onto the textarea inside the zone still fires
                    // dragleave on the zone; only a target outside it counts as
                    // actually leaving, or the highlight strobes.
                    if (!e.currentTarget.contains(e.relatedTarget)) this._over = false;
                }}
            >
                <textarea
                    id=${this.inputId}
                    class="form-control"
                    rows=${this.rows}
                    maxlength=${this.maxlength}
                    placeholder=${this.placeholder}
                    .value=${this.value}
                    @input=${() => this._emit()}
                    @paste=${(e) => this._handleImages(e)}
                ></textarea>
                ${
                    this._over
                        ? html`<div class="drop-overlay">
                              <i class="bi bi-image"></i>Drop to attach
                          </div>`
                        : nothing
                }
            </div>

            <div class="form-text d-flex align-items-center gap-2 flex-wrap">
                <span>
                    Markdown works here — **bold**, lists, \`code\` and links. Paste or drag an
                    image to attach it, or
                    <label class="text-decoration-underline" style="cursor: pointer">
                        choose a file
                        <input
                            type="file"
                            class="visually-hidden"
                            accept=${ATTACHABLE_TYPES.join(',')}
                            multiple
                            @change=${(e) => this._choose(e)}
                        />
                    </label>.
                </span>
                ${
                    this._uploading > 0
                        ? html`<span class="text-primary">
                              <span
                                  class="spinner-border spinner-border-sm me-1"
                                  role="status"
                              ></span>
                              Uploading…
                          </span>`
                        : nothing
                }
            </div>

            ${
                this._error
                    ? html`<div class="alert alert-warning py-1 px-2 mt-2 mb-0 small" role="alert">
                          ${this._error}
                      </div>`
                    : nothing
            }
        `;
    }
}

customElements.define('community-markdown-input', CommunityMarkdownInput);
